import type { VisualPayload } from "../../types";
import { isSafeUrl } from "../../lib/safeUrl";
import { TYPE_TEXT_COLORS } from "../../utils/typeColors";
import CardShell from "./_CardShell";
import PokemonSprite from "../PokemonSprite";

interface Props { payload: VisualPayload }

export default function LoreCard({ payload }: Props) {
  const header = payload.sections.find((s) => s.section === "header");
  const lore   = payload.sections.find((s) => s.section === "lore");

  const type1 = (header?.type1 as string | undefined) ?? "";
  const type2 = (header?.type2 as string | undefined) ?? "";
  const pokedexId = header?.pokedex_id as number | undefined;
  const description = (lore?.description as string | undefined) ?? "";
  const sourceUrl   = (lore?.source_url as string | undefined) ?? payload.source_url ?? "";

  return (
    <CardShell
      title={payload.title}
      source={payload.data_source}
      timestamp={payload.data_timestamp}
      sourceUrl={payload.source_url}
      shareUrl={payload.share_url}
      snapshotPayload={payload.snapshot_eligible ? payload : undefined}
      accent="border-violet-500/20"
    >
      {/* Header row — species name + type pills + Pokédex number */}
      {header && (
        <div className="flex items-start gap-3 mb-4">
          <PokemonSprite
            pokedexId={pokedexId}
            alt={header.pokemon_name as string}
            size="lg"
          />
          <div className="flex-1 min-w-0">
            <p className="text-lg font-bold text-white leading-tight">
              {header.pokemon_name as string}
            </p>
            {pokedexId != null && (
              <p className="text-xs text-white/40 mt-0.5">
                <span className="text-white/25">#</span>{pokedexId}
              </p>
            )}
            <div className="flex items-center gap-1.5 mt-1.5">
              {type1 && (
                <span className={`text-xs font-medium ${TYPE_TEXT_COLORS[type1.toLowerCase()] ?? "text-gray-300"}`}>
                  {type1}
                </span>
              )}
              {type2 && (
                <>
                  <span className="text-white/20">·</span>
                  <span className={`text-xs font-medium ${TYPE_TEXT_COLORS[type2.toLowerCase()] ?? "text-gray-300"}`}>
                    {type2}
                  </span>
                </>
              )}
            </div>
          </div>

          {/* Lore badge */}
          <span className="px-2 py-0.5 rounded-full bg-violet-500/10 border border-violet-500/20 text-violet-300 text-[11px] font-medium">
            Lore
          </span>
        </div>
      )}

      {/* Description */}
      {description && (
        <p className="text-sm text-white/70 leading-relaxed mb-4">{description}</p>
      )}

      {/* Bulbapedia attribution — Bulbapedia content is licensed under
          CC BY-NC-SA 2.5 (per https://bulbapedia.bulbagarden.net/wiki/
          Bulbapedia:Copyrights).  Attribution practice for CC reuse
          requires naming the source AND indicating the licence with
          a link to the licence deed — both surfaced here. */}
      {isSafeUrl(sourceUrl) && (
        <div className="mt-3 pt-3 border-t border-white/[0.06] space-y-1">
          <a
            href={sourceUrl}
            target="_blank"
            rel="noopener noreferrer"
            className="text-[11px] text-violet-400/70 hover:text-violet-300 transition-colors block"
          >
            Read more on Bulbapedia →
          </a>
          <p className="text-[10px] text-white/30">
            Text from Bulbapedia under{" "}
            <a
              href="https://creativecommons.org/licenses/by-nc-sa/2.5/"
              target="_blank"
              rel="noopener noreferrer"
              className="hover:text-white/50 transition-colors underline-offset-2 hover:underline"
            >
              CC BY-NC-SA 2.5
            </a>
          </p>
        </div>
      )}
    </CardShell>
  );
}
